// Кой продукт е в складовата карта и как се влиза/излиза от нея: клик или Enter
// на ред я отваря, ↑/↓ в тесния списък сменят продукта (`select`), Esc я
// затваря — с preventDefault, иначе същото натискане минава и през глобалната
// Esc навигация (SharedAccountGate) и изхвърля от модула. При отворен antd слой
// (модалът на документ, падащо меню, календар) Esc е негов. След затваряне
// фокусът се връща на реда на същия продукт в таблицата, за да продължи ↑/↓.
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { hasOpenOverlay } from "../../../../lib/overlay";
import type { StockRow } from "../components/stockShared";

type Options = {
  // всички редове на склада — след презареждане картата чете свежия ред
  allRows: StockRow[];
  // редовете на таблицата в реда на екрана — за връщането на фокуса
  visibleRows: StockRow[];
  // контейнерът на таблицата; редовете ѝ са `[data-row]` (reportListModel)
  tableRef: RefObject<HTMLElement | null>;
};

// Първо по изричен атрибут (ако таблицата го носи), иначе по позицията на реда
const tableRowOf = (container: HTMLElement | null, productId: number, index: number) =>
  container?.querySelector<HTMLElement>(`[data-row][data-product-id="${productId}"]`) ??
  container?.querySelectorAll<HTMLElement>("[data-row]")[index] ??
  null;

// Табът, избран в картата — Enter/→ от тесния списък влиза в нея
export const focusStockCard = () =>
  document
    .querySelector<HTMLElement>('[data-stock-card] [role="tab"][aria-selected="true"]')
    ?.focus();

export const useStockCardSelection = ({ allRows, visibleRows, tableRef }: Options) => {
  const [openedRow, setOpenedRow] = useState<StockRow | null>(null);
  const restoreFocusIdRef = useRef<number | null>(null);

  const selected = openedRow
    ? (allRows.find((row) => row.productId === openedRow.productId) ?? openedRow)
    : null;

  const open = useCallback((row: StockRow) => setOpenedRow(row), []);

  const close = useCallback(() => {
    restoreFocusIdRef.current = openedRow?.productId ?? null;
    setOpenedRow(null);
  }, [openedRow]);

  // Esc затваря картата, докато е отворена
  useEffect(() => {
    if (!openedRow) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented || hasOpenOverlay()) return;
      event.preventDefault();
      close();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [openedRow, close]);

  // Фокусът обратно на реда (таблицата се връща чак след рендера)
  useEffect(() => {
    const productId = restoreFocusIdRef.current;
    if (openedRow || productId == null) return;
    const index = visibleRows.findIndex((row) => row.productId === productId);
    // Ref-ът се чисти чак в кадъра: пререндер преди него отменя кадъра и
    // ефектът го пуска наново
    const frame = requestAnimationFrame(() => {
      restoreFocusIdRef.current = null;
      tableRowOf(tableRef.current, productId, index)?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [openedRow, visibleRows, tableRef]);

  return { selected, isOpen: !!openedRow, open, select: open, close };
};
